import Minilog from "minilog";
import StringMask from "string-mask";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import { FIELD_MASK, FLOW_POSITION } from "./const";

dayjs.extend(updateLocale);

dayjs.updateLocale("en", {
  months: [
    "Január",
    "Február",
    "Március",
    "Április",
    "Május",
    "Június",
    "Július",
    "Augusztus",
    "Szeptember",
    "Október",
    "November",
    "December",
  ],
});

export function isDevMode() {
  /* eslint-disable */
    return process.env.NODE_ENV === 'development';
}

export function newLogger(name) {
    if (isDevMode() === true) {
        Minilog.enable();
    }
    return Minilog(name || 'app');
}

export function generateNonce() {
  let result = [];
  let characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let charactersLength = characters.length;
  for (let i = 0; i < 16; i++) {
    result.push(
      characters.charAt(Math.floor(Math.random() * charactersLength))
    );
  }
  return result.join("");
}

export function monify(number) {
  if (number === null || number === undefined) return 0;
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export function formatDate(date) {
  if (!date) {
    return undefined;
  }
  return dayjs(date).format('YYYY-MM-DD');
}

export function formatDateTime(date) {
  if (!date) {
    return undefined;
  }
  return dayjs(date).format('YYYY.MM.DD. HH:mm');
}

export const localizedFormatter = (locale) => {
  return {
    date(date) {
      try {
        return new Intl.DateTimeFormat(locale, {
          dateStyle: "short",
        }).format(new Date(date));
      } catch {
        return "";
      }
    },
    time(date) {
      try {
        return new Intl.DateTimeFormat(locale, {
          timeStyle: "short",
        }).format(new Date(date));
      } catch {
        return "";
      }
    },
    dateTime(date) {
      try {
        return new Intl.DateTimeFormat(locale, {
          dateStyle: "short",
          timeStyle: "short",
        }).format(new Date(date));
      } catch {
        return "";
      }
    },
    money(currency, amount) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        trailingZeroDisplay: "stripIfInteger",
      }).format(amount);
    },
    percent(value) {
      return new Intl.NumberFormat(locale, {
        style: "percent",
        roundingPriority: "lessPrecision",
      }).format(value);
    }
  }
}

export function maskValue(mask, value) {
  if (mask === FIELD_MASK.HUF) {
    if (value) {
      let valueForFormat = typeof value === "string" ? value.replace(/\D/g,'') : value;
      const monified = monify(valueForFormat);
      if (monified === 0) return "";
      return monified;
    }
  } else if (mask === FIELD_MASK.PHONE) {
    if (value) {
      let valueForFormat = typeof value === "string" ? value.replace("+36", "").replaceAll(" ", "") : value;
      let apply = new StringMask("+36 00 000 0000").apply(valueForFormat);
      return apply ? apply : "+36";
    } else {
      return "+36";
    }
  }
  return value;
}

export function applyCustomStringMask(mask, value) {
  return new StringMask(mask).apply(value);
}

export function formatBooleanRow(checked) {
  return `<label class="switch is-rounded" disabled="disabled">
                <input type="checkbox" disabled ${checked ? "checked" : ""}>
                <span class="check is-blue"></span>
              </label>`
}

export function nextStepConfigOverride(flowConfig, currentPosition) {
  if (currentPosition === FLOW_POSITION.OVERVIEW
    && flowConfig.dataValidationRequired) {
    return FLOW_POSITION.DATA_VALIDATION;
  } else if (
    (currentPosition === FLOW_POSITION.OVERVIEW || currentPosition === FLOW_POSITION.DATA_VALIDATION)
    && flowConfig.identificationRequired) {
    return FLOW_POSITION.KYC_PREPARATION;
  } else if ((currentPosition === FLOW_POSITION.OVERVIEW || currentPosition === FLOW_POSITION.DATA_VALIDATION
      || currentPosition === FLOW_POSITION.KYC_PREPARATION)
    && flowConfig.contractCheckRequired) {
    return FLOW_POSITION.CONTRACT_CHECK;
  } else if ((currentPosition === FLOW_POSITION.OVERVIEW || currentPosition === FLOW_POSITION.DATA_VALIDATION
      || currentPosition === FLOW_POSITION.KYC_PREPARATION || currentPosition === FLOW_POSITION.CONTRACT_CHECK)
    && flowConfig.signatureRequired) {
    return FLOW_POSITION.SIGN_PREPARATION;
  } else {
    return FLOW_POSITION.RESULT;
  }
}

export function validateVatNumber(vatNumber) {
  if (!vatNumber) {
    return false;
  }

  var pattern = /^(\d{7})(\d)\-([1-5])\-(0[2-9]|[13][0-9]|2[02-9]|4[0-4]|51)$/;
  var matches = vatNumber.match(pattern);
  if (matches) {
    // Szorzók az adószám törzsszám része (első 8 jegy) ellenőrző számjegyének
    // meghatározásához (a 8. jegy az első hét jegy alapján). Az adott szorzóval, a
    // törzsszám megfelelő számjegyét (elölről indulva) megszorozzuk, majd ezen
    // szorzatok összegét képezzük.
    var mul = [9, 7, 3, 1, 9, 7, 3];
    // Az adószám törzsszám részének első 7 jegye.
    var base = matches[1].split("");
    // Ellenőrző számjegy (az adószám 8. jegye).
    var check = parseInt(matches[2]);
    // Ellenőrző összeg meghatározása.
    var sum = 0;
    for (var i = 0; i < 7; i++) { sum += parseInt(base[i]) * mul[i]; }
    // Az ellenőrző összeg utolsó jegyének meghatározása (= 10-zel való osztás maradéka).
    var last = sum % 10;
    // Ha a kiszámított ellenőrző számjegy nem nulla, akkor 10-ből kivonjuk.
    if (last > 0) { last = 10 - last; }
    // A kiszámított és kapott ellenőrző számjegyek egyezősége alapján
    // lesz helyes vagy helytelen az adószám.
    return last === check;
  }
  return false;
}

// Call this in mounted not in created
export async function resetValidator(observer) {
  await observer.validate();
  observer.reset();
}

export function formatMoney(amount) {
  if (!amount || isNaN(amount) ) return 0;

  return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") + " Ft";
}

export function findParentRef(that, ref) {
  let refObject = null
  let parent = that;
  while (refObject == null && parent != null) {
    refObject = parent.$refs[ref];
    parent = parent.$parent;
  }
  return refObject
}